import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { ListItemState } from '../../../components/ListWithContentLayout/types';

import { type ArrayFields } from '../../License/types/fieldEnums';
import { type IDifference } from '../../../store/audit/types';

import { versionComparisonSelector } from '../../../store/audit/selectors';
import createMapByProperty from '../../../helpers/createMapByProperty';
import useComparisonParams from './useComparisonParams';

function useListItems<T extends object>(data: T[], entityName: ArrayFields) {
  const { differences } = useSelector(versionComparisonSelector);
  const { leftEpochMillis, rightEpochMillis } = useComparisonParams();

  return useMemo(() => {
    const listItemsMap = createMapByProperty(data ?? []);
    const listItemStates = {} as Record<string, ListItemState>;

    differences.forEach((difference: IDifference) => {
      if (difference.path.includes(`/${entityName}`)) {
        const [, , listItemId] = difference.path.split('/') as [string, string, string];

        const listItemPathRegex = new RegExp(`^\\/${entityName}\\/[0-9]+$`);

        if (listItemPathRegex.test(difference.path)) {
          const listItemIsAdded =
            Number(leftEpochMillis) < Number(rightEpochMillis) ? !listItemsMap[listItemId] : !!listItemsMap[listItemId];

          listItemStates[listItemId] = listItemIsAdded ? ListItemState.Added : ListItemState.Deleted;

          if (!listItemsMap[listItemId]) {
            listItemsMap[listItemId] = (difference.leftValue ?? difference.rightValue) as T;
          }
        } else {
          listItemStates[listItemId] = ListItemState.Changed;
        }
      }
    });

    const listItems = Object.values(listItemsMap);

    return [listItems, listItemStates] as [T[], Record<string, ListItemState>];
  }, [data, differences, entityName, leftEpochMillis, rightEpochMillis]);
}

export default useListItems;
